import { DateRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Tooltip from '@/components/UI/Tooltip';
import { _isNil } from '@/littledash';
import cn from 'classnames';
import type { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { DateUtils } from '@/utils/Date.utils';
import styles from '../DataTableV2.module.scss';
import { PickerDisplayFormat, usePickerCellBehavior } from '../hooks/usePickerCellBehavior';
import { useAutoInsertOnActivate } from '../hooks/useAutoInsertOnActivate';

interface DataTableDateCellProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

/**
 * Date-only picker cell for metadata date fields.
 *
 * Uses `<input type="date">` with `PickerDisplayFormat.Date` — values are stored as
 * raw `YYYY-MM-DD` strings with no timezone conversion (unlike datetime cells).
 *
 * Only the `Loading` cell state shows a loading indicator. The `Saving` state is not
 * treated as loading because date values are persisted directly — there is no
 * server-side calculation that would produce a different display value after save.
 */
export const DataTableDateCell: FC<DataTableDateCellProps> = ({ coordinate, positionOffset }) => {
  const { cell, picker, mouseHandlers } = usePickerCellBehavior({
    coordinate,
    positionOffset,
    displayFormat: PickerDisplayFormat.Date,
  });

  useAutoInsertOnActivate({
    coordinate,
    getValue: () => DateUtils.dateNow(),
  });

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: cell.hasGroupBorder })}
      style={picker.positionStyle}
      onMouseDown={mouseHandlers.onMouseDown}
      onMouseEnter={mouseHandlers.onMouseEnter}
      onMouseUp={mouseHandlers.onMouseUp}
      onDoubleClick={picker.activate}
    >
      {cell.state === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={cell.tooltip}>
          <div
            className={cn(
              styles.Cell,
              styles[cell.selectionState],
              { [styles.Active]: picker.active },
              styles[cell.state]
            )}
            data-state={cell.state}
            data-test-component="DataTableDateCell"
            data-test-element="date-cell"
            data-test-key={`(${coordinate.column},${coordinate.row})`}
          >
            <span className={styles.TimestampValue}>
              {_isNil(cell.value) ? <>Add Date</> : <DateRenderer value={cell.value} defaultResponse={cell.value} />}
            </span>
            <input
              className={styles.TimestampInput}
              style={{ visibility: picker.active ? 'visible' : 'hidden' }}
              ref={picker.inputRef}
              type="date"
              onChange={picker.handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
